import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form943DueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Difference between Form 941 and Form 944 | TaxZerone "
          description="Learn the key differences between IRS Form 940 and Form 944 to ensure accurate filing for your business. E-File with TaxZerone and stay compliant easily."
          keywords=""
          canonical="resources/employment-tax/form-941-vs-form-944/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          Understanding the Difference Between
          <br />
          <span className="highlight-container">
            <span className="highlight">Form 941 </span>
          </span>{' '}
          and{' '}
          <span className="highlight-container">
            <span className="highlight">944 </span>
          </span>{' '}
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-941-vs-form-944/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 941 vs. Form 944 at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-941-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>Form 941</b>
                        </Link>
                        {' and '}
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-944-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>Form 944</b>
                        </Link>
                        {
                          ' are both used by employers to report federal payroll taxes, but they differ '
                        }
                        {'mainly in'}{' '}
                        <b>filing frequency and employer eligibility</b>.
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Form 941:</b>
                        {' Filed '}
                        <b>quarterly</b>
                        {
                          ' by most employers to report federal income tax withholding and Social Security and Medicare taxes. '
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Form 944:</b>
                        {' Filed '}
                        <b>annually</b>
                        {
                          ' by eligible small employers with an annual employment tax liability of '
                        }
                        <b>$1,000 or less</b>
                        {'. '}{' '}
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Form 941 Deadlines:</b> April 30, July 31, October
                        31, and January 31
                        {'. '}
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Form 944 Deadline:</b> January 31
                        {'. '}
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Note</b>
                        <b>:</b>
                        {
                          ' Employers cannot switch between Form 941 and Form 944 on their own. IRS authorization is '
                        }
                        required
                        {' to use Form 944. '}
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  If you are an employer, filing employment tax forms on time is
                  essential to stay compliant with the IRS. Two commonly used
                  forms are{' '}
                  <Link
                    href="/resources/employment-tax/form-941-instructions/"
                    sx={{ color: '#2020ff' }}
                  >
                    Form 941
                  </Link>{' '}
                  and{' '}
                  <Link
                    href="/resources/employment-tax/form-944-instructions/"
                    sx={{ color: '#2020ff' }}
                  >
                    Form 944
                  </Link>
                  , but they are not the same. Knowing which one applies to your
                  business can save you time, effort, and penalties.
                </Typography>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box />
                  {/* <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  ></Box> */}
                </Stack>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 941?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form" sx={{ color: '#0202ff' }}>
                          What is Form 944?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          Key differences for Form 941 Vs Form 944
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common scenarios
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="what"
                >
                  What is Form 941?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <Link
                    href="/resources/employment-tax/form-941-instructions/"
                    sx={{ color: '#2020ff' }}
                  >
                    Form 941
                  </Link>{' '}
                  (Employer’s Quarterly Federal Tax Return) is used by U.S.
                  employers to report and pay federal payroll taxes on a
                  quarterly basis. It includes information on federal income tax
                  withheld from employees wages, Employer’s and Employee’s share
                  of Social Security and Medicare taxes.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Employers are required to file Form 941 every
                  quarter, even if you have no taxes to report for that period.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="form"
                >
                  What is Form 944?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <Link
                    href="/resources/employment-tax/form-944-instructions/"
                    sx={{ color: '#2020ff' }}
                  >
                    {' '}
                    Form 944
                  </Link>{' '}
                  (Employer’s Annual Federal Tax Return) is used by small
                  businesses with an annual payroll tax liability of $1,000 or
                  less. It allows eligible employers to report federal income
                  tax, Social Security, and Medicare taxes annually instead of
                  quarterly.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Simplify your employment tax filing with TaxZerone!
                      </Typography>
                      <Typography variant="body2">
                        Whether it&apos;s Form 941 or 944, TaxZerone helps you
                        file with confidence and ease.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941vs944`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="key"
                >
                  Key differences of Form 941 Vs Form 944
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see the differences between Form 941 Vs Form 944
                </Typography>

                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Feature
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Form 941
                          </TableCell>
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Form 944
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Filing Frequency </TableCell>

                          <TableCell>Quarterly (4 times/year)</TableCell>
                          <TableCell>Annually (1 time/year)</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Eligibility</TableCell>

                          <TableCell>Most employers</TableCell>
                          <TableCell>
                            Small employers with low annual tax liability
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>IRS Notification Required</TableCell>

                          <TableCell>
                            No — employers file based on their liability or IRS
                            instruction
                          </TableCell>
                          <TableCell>
                            Yes — employers must receive IRS approval to file
                            Form 944
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Tax Liability Threshold </TableCell>

                          <TableCell>Greater than $1000 </TableCell>
                          <TableCell>$1,000 or less annually</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            <Link
                              href="/resources/employment-tax/form-941-944-deposit-requirements/"
                              sx={{ color: '#2020ff' }}
                            >
                              Deposit Schedule
                            </Link>
                          </TableCell>

                          <TableCell>
                            <Typography
                              variant="body1"
                              sx={{ mb: 1, fontSize: 14 }}
                            >
                              <strong>Monthly:</strong> $50,000 or less in taxes
                              during the lookback period
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ mb: 1, fontSize: 14 }}
                            >
                              <strong>Semiweekly:</strong> More than $50,000 in
                              taxes during the lookback period
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body1"
                              sx={{ mb: 1, fontSize: 14 }}
                            >
                              <strong>No deposits: </strong> If total liability
                              is under $2,500 for the year (can pay with return)
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ mb: 1, fontSize: 14 }}
                            >
                              <strong>Last day of Quarter end: </strong> If
                              liability is $2,500 or more for the year
                            </Typography>
                            <Typography variant="body1" sx={{ fontSize: 14 }}>
                              <strong>Monthly or Semiweekly:</strong> If
                              liability is $2,500 or more for the quarter
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Deadline </TableCell>

                          <TableCell>
                            <ul style={{ marginBottom: 20 }}>
                              <li style={{ marginBottom: 12 }}>April 30*</li>
                              <li style={{ marginBottom: 12 }}>July 31*</li>
                              <li style={{ marginBottom: 12 }}>October 31*</li>
                              <li style={{ marginBottom: 12 }}>
                                January 31*
                              </li>{' '}
                            </ul>
                          </TableCell>
                          <TableCell>January 31*</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="body2" sx={{ mt: 1, color: '#6f6c7f' }}>
                    * If a deadline falls on a weekend or federal holiday, the
                    return is due on the next business day.
                  </Typography>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Which form is right for your business: 941 or 944?
                </Typography>

                <ul style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>
                      {' '}
                      Choose{' '}
                      <Link
                        href="/resources/employment-tax/form-941-instructions/"
                        sx={{ color: '#2020ff' }}
                      >
                        Form 941
                      </Link>{' '}
                      if:{' '}
                    </b>
                  </li>
                  <ul style={{ marginBottom: 20 }}>
                    <li style={{ marginBottom: 12 }}>
                      Your business withholds more than{' '}
                      <b>$1,000 in employment taxes</b> annually.
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      You have a moderate or large payroll.
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      You are required to file <b>quarterly</b> (every three
                      months).
                    </li>
                  </ul>
                  <li style={{ marginBottom: 12 }}>
                    <b>
                      Choose{' '}
                      <Link
                        href="/resources/employment-tax/form-944-instructions/"
                        sx={{ color: '#2020ff' }}
                      >
                        Form 944
                      </Link>{' '}
                      if:
                    </b>
                  </li>
                  <ul style={{ marginBottom: 20 }}>
                    <li style={{ marginBottom: 12 }}>
                      Your total annual liability for federal income, Social
                      Security, and Medicare taxes is <b>$1,000 or less.</b>
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      You have a very small payroll.
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      The <b>IRS has notified you</b> that you are eligible to
                      file Form 944.
                    </li>
                  </ul>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 87,
                  }}
                  id="common"
                >
                  Common scenarios
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  1.Can I switch from Form 941 to Form 944?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Employers cannot switch from Form 941 to Form 944 on their
                  own. The IRS must authorize the use of Form 944 based on the
                  employer&apos;s annual tax liability.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  2. I received a letter from the IRS saying I can file Form
                  944. Can I choose to file Form 941 instead?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the IRS has directed you to{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-944/"
                    sx={{ color: '#2020ff' }}
                  >
                    file Form 944
                  </Link>
                  , you cannot{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#2020ff' }}
                  >
                    file Form 941
                  </Link>{' '}
                  unless you request and receive approval from the IRS to
                  switch.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  3. My business only operates during the summer months. Do I
                  still need to file all four quarters of Form 941?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To avoid filing for inactive quarters, check the &quot;
                  <b>Seasonal Employer</b>&quot; box on <b>Line 18</b> of the
                  form. If your business operates only during the summer months,
                  you are <b>not required</b> to file all four quarters of Form
                  941. However, you must file for the quarters in which you paid
                  wages to employees.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  4. I have closed my business mid-year. Do I still need to file
                  Form 941 for the remaining quarters?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You need to file a final Form 941 for the last quarter you
                  paid wages and check the box on Line 17 to indicate it is your
                  final return. Attach a payroll statement including your name,
                  and the address where the data is stored.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  5. Can I request to switch from filing Form 941 to Form 944 if
                  my business has slowed down?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Yes, but you must request the change with the IRS before April
                  1 of the current tax year, and the IRS must approve it before
                  you begin filing Form 944.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        IRS website. For more information, check the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i941.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>{' '}
                        instructions for Form 941 and{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i944.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>{' '}
                        instructions for Form 944.{' '}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file Form 944 with TaxZerone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Whether you are filing quarterly <b>Form 941</b> or annual{' '}
              <b>Form 944,</b>
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              TaxZerone makes it simple, secure, and affordable.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941vs944`}
              >
                E-File Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form943DueDates;
